// Source de vérité visuelle (doc 05 §2, révisé par branding/v2/da-v2.md) — aucune
// valeur brute de design hors de ce package.

// ---------------------------------------------------------------------------
// §2.1 Primitives — neutres
// ---------------------------------------------------------------------------
//
// La rampe est monotone : 0 = blanc, 950 = noir de scène. Les cinq valeurs claires
// 50→150 viennent de la DA v2 (§1.1) : le canvas n'est PAS blanc pur, sans quoi les
// cartes blanches n'auraient plus que l'ombre pour s'en détacher — et l'ombre est
// justement interdite en sombre. C'est ce qui rend les deux thèmes structurellement
// identiques.

export const color = {
  gray: {
    0: '#FFFFFF', // bg.surface clair (les cartes)
    50: '#F2F2F5', // bg.canvas clair
    75: '#EDEDF1', // bg.chip clair
    100: '#E9E9EE', // bg.sunk clair
    150: '#E4E4EA', // border.default clair
    300: '#B4B5BB', // texte désactivé (clair)
    400: '#87888F', // text.tertiary clair (3,53:1 sur blanc) + border.strong clair
    450: '#7E7F88', // text.tertiary sombre (4,62:1 sur gray.900)
    500: '#6A6B74', // text.secondary clair (4,74:1 sur canvas)
    600: '#5D5E66', // icônes clair, texte désactivé sombre
    800: '#26262B', // bg.chip sombre, surface 3
    850: '#1C1C21', // bg.surface sombre
    900: '#141417', // bg.canvas sombre (gestion)
    925: '#101013', // bg.sunk sombre
    950: '#0B0B0E', // fond display (noir de scène, pas #000 pur)
  },
  red: { 500: '#E5484D', 700: '#B3261E' },
  green: { 500: '#2FBF71', 700: '#1B7A48' },
  blue: { 500: '#3E8BFF' },
} as const;

// ---------------------------------------------------------------------------
// §2.1 bis — SIGNATURE : « Framboise orangée » (branding/v2/da-v2.md §2)
// ---------------------------------------------------------------------------
//
// L'or de scène `accent.*` a disparu le jour où les apps ont basculé sur la DA v2 :
// `brand.*` est désormais la seule rampe de commande ET d'identité. Elle est RARE —
// CTA, focus, onglet actif, point live, rail du leader. Jamais décorative, jamais en
// aplat de carte (c'est le rôle des 16 couleurs d'équipes, §3.1).
export const brand = {
  tint: '#FFEDF2', // fonds teintés, ligne du leader
  300: '#FFB3C9', // texte/lien SUR SOMBRE
  400: '#FF7A9E', // stop clair des dégradés, actif sombre, encre du wordmark sur sombre
  500: '#F0407A', // LA signature : onglet actif, point live, halo, rail du leader
  600: '#D42A67', // pressed, filet, CTA sur thème clair
  700: '#A31A4C', // texte/lien SUR CLAIR (6,69:1 sur blanc)
} as const;

/** Encre de marque : le noir du wordmark et du symbole monochrome (da-v2 §6). */
export const brandInk = '#101014';

/** Les deux seules recettes de CTA autorisées (da-v2 §2.2), ratios mesurés. */
export const brandCta = {
  light: { bg: brand[600], ink: '#FFFFFF' }, // 4,85:1
  dark: { bg: '#FF5C8D', ink: '#2A0512' }, // 6,35:1
} as const;

/**
 * Dégradés de marque — liste FERMÉE (da-v2 §3 : quatre emplois, pas un de plus).
 * `pastille` est celui du logo : c'est le seul que consomme `brand.ts`.
 */
export const brandGradient = {
  /** Pastille d'icône, 135° — le dégradé du symbole « +1 ». */
  pastille: { angle: 135, from: '#FF7A45', to: '#E0357A' },
  /** Sunburst, thème clair : centre → milieu → bord. */
  sunburstLight: ['#FFD9B8', '#F5527E', '#B92C86'],
  /** Sunburst, thème sombre : projecteur → pénombre → `gray.950`. */
  sunburstDark: ['#F0407A', '#5E1140', '#0B0B0E'],
} as const;

// ---------------------------------------------------------------------------
// §2.2 Sémantiques — deux thèmes, un seul jeu de composants (da-v2 §1)
// ---------------------------------------------------------------------------
//
// Le CLAIR est le défaut de toutes les surfaces de gestion (webapp, back-office,
// participant, mobile) ; le sombre est au choix. Le display, lui, part du sombre :
// il pose son sunburst par-dessus (`sunburst`, plus bas) et force `gray.950` en fond.
//
// La bascule n'est pas un second design : mêmes composants, mêmes dimensions, mêmes
// rayons. Seul ce jeu de valeurs change.

export interface SemanticTokens {
  /**
   * `sunk` = creux (inputs, pistes) ; `chip` = pilules et fonds neutres de commande ;
   * `tint` = le fond de la LIGNE DISTINGUÉE (leader, « c'est moi ») — teinté de la
   * signature en clair, éclairci au blanc en sombre, jamais l'inverse.
   */
  bg: {
    canvas: string;
    surface: string;
    surfaceRaised: string;
    sunk: string;
    chip: string;
    tint: string;
  };
  border: { default: string; strong: string };
  /** `tertiary` : chevrons, rangs du display, légendes — JAMAIS une phrase (da-v2 §7). */
  text: { primary: string; secondary: string; tertiary: string; disabled: string };
  action: { primary: string; primaryText: string; primaryHover: string; link: string };
  feedback: { danger: string; success: string; info: string };
  focus: { ring: string; ringWidth: number; ringOffset: number };
  /** Verre dépoli : les 3 barres flottantes et le bouton rond. Jamais une carte. */
  glass: { bg: string; border: string };
  /** Pilule opaque posée sous un texte qui tombe sur un dégradé (da-v2 §4). */
  scrim: string;
  /** Élévation : deux niveaux en clair, AUCUNE ombre en sombre (luminance + bordure). */
  shadow: { 1: string; 2: string };
}

export type Theme = 'dark' | 'light';

/** Élévation du thème clair — deux niveaux, pas un de plus (da-v2 §1.1). */
export const shadow = {
  1: '0 1px 2px #10101410, 0 6px 16px #1010140D', // cartes
  2: '0 2px 4px #1010140F, 0 14px 34px #10101414', // menus, dialogs, toasts
} as const;

export const semantic: Record<Theme, SemanticTokens> = {
  dark: {
    bg: {
      canvas: color.gray[900], // gestion ; le display force gray.950 (voir tvBackground)
      surface: color.gray[850],
      surfaceRaised: color.gray[800],
      sunk: color.gray[925],
      chip: color.gray[800],
      tint: 'rgba(255, 255, 255, 0.17)',
    },
    border: { default: '#FFFFFF1A', strong: '#FFFFFF29' },
    text: {
      primary: '#F4F4F6',
      secondary: '#A7A8B0', // 7,77:1 sur gray.900
      tertiary: color.gray[450], // 4,62:1
      disabled: color.gray[600],
    },
    action: {
      primary: brandCta.dark.bg,
      primaryText: brandCta.dark.ink,
      primaryHover: brand[400],
      link: brand[300],
    },
    feedback: { danger: color.red[500], success: color.green[500], info: color.blue[500] },
    focus: { ring: brand[500], ringWidth: 2, ringOffset: 2 },
    glass: { bg: 'rgba(38, 38, 43, 0.60)', border: 'rgba(255, 255, 255, 0.14)' },
    scrim: 'rgba(255, 255, 255, 0.12)',
    shadow: { 1: 'none', 2: 'none' },
  },
  light: {
    bg: {
      canvas: color.gray[50],
      surface: color.gray[0],
      surfaceRaised: color.gray[0], // + shadow.1
      sunk: color.gray[100],
      chip: color.gray[75],
      tint: brand.tint,
    },
    border: { default: color.gray[150], strong: color.gray[400] },
    text: {
      primary: brandInk,
      secondary: color.gray[500], // 4,74:1 sur canvas
      tertiary: color.gray[400], // 3,53:1 sur blanc
      disabled: color.gray[300],
    },
    action: {
      primary: brandCta.light.bg,
      primaryText: brandCta.light.ink,
      primaryHover: '#B92258',
      link: brand[700],
    },
    feedback: { danger: color.red[700], success: color.green[700], info: color.blue[500] },
    focus: { ring: brand[500], ringWidth: 2, ringOffset: 2 },
    glass: { bg: 'rgba(255, 255, 255, 0.72)', border: 'rgba(255, 255, 255, 0.85)' },
    scrim: 'rgba(255, 255, 255, 0.92)',
    shadow,
  },
};

// Le display force son fond de scène (doc 05 §9.1 : `.tv { --bg-canvas: #0B0B0E }`).
export const tvBackground = color.gray[950];

/**
 * Sunburst du display (da-v2 §2.3) — la géométrie est IDENTIQUE dans les deux thèmes,
 * seule la rampe change. Deux couches, aucune animée : le dégradé radial pour la masse
 * lumineuse, les rayons coniques par-dessus. C'est ce qui remplace l'aplat noir du
 * doc 05 sans coûter une frame sur une TV de 2013.
 */
export const sunburst: Record<Theme, { gradient: string; rays: string }> = {
  light: {
    gradient:
      'radial-gradient(ellipse 92% 125% at 50% 38%, ' +
      `${brandGradient.sunburstLight[0]} 0%, ${brandGradient.sunburstLight[1]} 46%, ` +
      `${brandGradient.sunburstLight[2]} 100%)`,
    rays: raysGradient(0.15),
  },
  dark: {
    gradient:
      'radial-gradient(ellipse 74% 100% at 50% 38%, ' +
      `${brandGradient.sunburstDark[0]} 0%, ${brandGradient.sunburstDark[1]} 38%, ` +
      `${brandGradient.sunburstDark[2]} 72%), ${color.gray[950]}`,
    rays: raysGradient(0.038),
  },
};

/** Les rayons : une seule couche, jamais animée (da-v2 §2.3). */
function raysGradient(opacity: number): string {
  return (
    'repeating-conic-gradient(from 0deg at 50% 38%, ' +
    `rgba(255, 255, 255, ${opacity}) 0deg 5.2deg, transparent 5.2deg 12deg)`
  );
}

// ---------------------------------------------------------------------------
// §2.3 Typographie
// ---------------------------------------------------------------------------

export const fontFamily = {
  interface: 'Inter Variable', // texte d'interface + tous les chiffres (tnum obligatoire)
  display: 'Archivo Variable', // titres display, noms d'équipes, moments de show
} as const;

export const fontWeight = {
  inter: { regular: 400, medium: 500, semibold: 600 },
  archivo: { bold: 700, black: 900 },
} as const;

export interface TypeStyle {
  size: number; // px
  lineHeight: number; // px
  family?: keyof typeof fontFamily;
  weight?: number;
  tabularNums?: boolean;
  letterSpacing?: string;
}

// Échelle gestion (mobile + desktop), en px
export const font: Record<string, { mobile: TypeStyle; desktop: TypeStyle | null }> = {
  caption: { mobile: { size: 12, lineHeight: 16 }, desktop: { size: 12, lineHeight: 16 } },
  'body-sm': { mobile: { size: 14, lineHeight: 20 }, desktop: { size: 13, lineHeight: 18 } },
  body: { mobile: { size: 16, lineHeight: 24 }, desktop: { size: 14, lineHeight: 20 } },
  'title-sm': { mobile: { size: 18, lineHeight: 24 }, desktop: { size: 16, lineHeight: 22 } },
  title: { mobile: { size: 22, lineHeight: 28 }, desktop: { size: 20, lineHeight: 26 } },
  'score-pad': {
    mobile: { size: 28, lineHeight: 32, family: 'interface', weight: 600, tabularNums: true },
    desktop: null, // télécommande mobile uniquement
  },
  // Le gros titre noir est LA signature typographique du chrome clair (da-v2 §3) :
  // même taille sur mobile et sur desktop, interlettrage serré, graisse 800.
  'display-hero': {
    mobile: { size: 38, lineHeight: 40, family: 'display', weight: 800, letterSpacing: '-0.032em' },
    desktop: {
      size: 38,
      lineHeight: 40,
      family: 'display',
      weight: 800,
      letterSpacing: '-0.032em',
    },
  },
};

export interface TvTypeStyle {
  size: string; // vh — l'écran se lit à 10 m, jamais de px fixes
  family: keyof typeof fontFamily;
  weight: number;
  tabularNums?: boolean;
  letterSpacing?: string;
  maxChars?: number;
}

// Échelle display TV, en vh
export const tv: Record<string, TvTypeStyle> = {
  chrome: { size: '2.6vh', family: 'interface', weight: 600 },
  code: { size: '3.2vh', family: 'interface', weight: 600, letterSpacing: '.12em' },
  rank: { size: '3.6vh', family: 'interface', weight: 600, tabularNums: true },
  name: { size: '4.2vh', family: 'display', weight: 700, maxChars: 14 }, // ellipse à 14 caractères
  score: { size: '5.2vh', family: 'interface', weight: 600, tabularNums: true },
  delta: { size: '3vh', family: 'interface', weight: 600 },
  'podium-name': { size: '8.5vh', family: 'display', weight: 900 },
  'podium-score': { size: '6.5vh', family: 'interface', weight: 600, tabularNums: true },
  'waiting-code': { size: '9vh', family: 'interface', weight: 600, letterSpacing: '.1em' },
  // Ligne du leader (da-v2 §5) : un AJOUT à l'échelle, pas un remplacement — les
  // quatre autres lignes gardent `tv.name` / `tv.score`.
  'leader-name': { size: '4.8vh', family: 'display', weight: 700, maxChars: 14 },
  'leader-score': { size: '6vh', family: 'interface', weight: 600, tabularNums: true },
};

// ---------------------------------------------------------------------------
// §2.4 Espacements et rayons — base 4 px (le display convertit en vh, 1 unité ≈ 0.5vh)
// ---------------------------------------------------------------------------

export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  6: 24,
  8: 32,
  12: 48,
  16: 64,
} as const;

// Le langage DA v2 est arrondi (§3) : on garde une échelle DIFFÉRENCIÉE, on déplace le
// curseur. La pilule est réservée aux commandes ; une carte n'est jamais une pilule.
export const radius = {
  sm: 10, // inputs, cellules back-office
  md: 18, // lignes de liste, surfaces de gestion, lignes de classement display
  card: 22, // cartes de contenu
  lg: 26, // blocs héros, sheets mobiles, bloc d'équipe sélectionnée
  full: 999, // chips, badges, avatars ET tous les boutons de commande (CTA, pad, barres)
} as const;

// ---------------------------------------------------------------------------
// §2.5 Élévation — voir `shadow` et `semantic[].shadow` (§2.2) : deux niveaux en
// clair, aucune ombre en sombre (l'élévation y est un delta de luminance + bordure).
// ---------------------------------------------------------------------------

/** Halo réservé aux moments de show (thème sombre) : `0 0 24px {couleurÉquipe}66`. */
export function glowShow(teamHex: string): string {
  return `0 0 24px ${teamHex}66`;
}

// ---------------------------------------------------------------------------
// §2.6 Motion — durées et courbes
// ---------------------------------------------------------------------------

export const motion = {
  duration: {
    instant: 80, // feedback de tap (pressed state)
    quick: 140, // toggles, chips, hover
    standard: 240, // transitions d'écrans gestion, dialogs
    gentle: 400, // réordonnancement de listes de gestion
    show: 700, // dépassement sur le display
    podium: 1200, // chaque marche du podium
    roulette: 3200, // décélération de la roulette
  },
  ease: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)', // défaut partout
    exit: 'cubic-bezier(0.4, 0, 1, 1)', // sorties (rapides, sans rebond)
    show: 'cubic-bezier(0.16, 1, 0.3, 1)', // expo-out, entrées display
    overshoot: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // dépassement, deltas de score (web)
    roulette: 'cubic-bezier(0.12, 0.8, 0.2, 1)', // roue de tirage
    spring: { damping: 18, stiffness: 220 }, // équivalent Reanimated (RN)
  },
} as const;

// prefers-reduced-motion (doc 05 §9.1)
export const motionReduced = {
  show: 240,
  podium: 600,
} as const;
